import { useTranslation } from '@dukanti/shared';
import type { AdminOrderReviewFilter, OrderStatus } from '@dukanti/shared';
import { cn } from '@/lib/utils';

export interface OrdersFilterChipsProps {
  readonly selected: OrderStatus | null;
  readonly selectedReviewFilter?: AdminOrderReviewFilter | null;
  readonly onChange: (status: OrderStatus | null) => void;
  readonly onReviewFilterChange?: (reviewFilter: AdminOrderReviewFilter) => void;
}

export function OrdersFilterChips({
  selected,
  selectedReviewFilter = null,
  onChange,
  onReviewFilterChange,
}: OrdersFilterChipsProps): React.JSX.Element {
  const { t } = useTranslation();
  const isReviewFilterActive = selectedReviewFilter !== null;

  return (
    <div
      data-testid="orders-filter-chips"
      role="radiogroup"
      aria-label={t('admin.orders.filter.groupLabel')}
      className="flex items-center gap-2 overflow-x-auto"
    >
      <FilterChip
        label={t('admin.orders.filter.all')}
        isActive={!isReviewFilterActive && selected === null}
        onClick={() => onChange(null)}
        testId="orders-filter-all"
      />
      {(
        [
          { status: 'ordered', label: t('admin.orders.filter.ordered') },
          { status: 'shipped', label: t('admin.orders.filter.shipped') },
          { status: 'delivered', label: t('admin.orders.filter.delivered') },
          { status: 'cancelled', label: t('admin.orders.filter.cancelled') },
          { status: 'rejected', label: t('admin.orders.filter.rejected') },
        ] as const
      ).map(({ status, label }) => (
        <FilterChip
          key={status}
          label={label}
          isActive={!isReviewFilterActive && selected === status}
          onClick={() => onChange(status)}
          testId={`orders-filter-${status}`}
        />
      ))}
      <FilterChip
        label={t('admin.orders.filter.awaitingApproval' as never)}
        isActive={selectedReviewFilter === 'awaiting_approval'}
        onClick={() => onReviewFilterChange?.('awaiting_approval')}
        testId="orders-filter-awaiting-approval"
      />
    </div>
  );
}

interface FilterChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}

function FilterChip({ label, isActive, onClick, testId }: FilterChipProps): React.JSX.Element {
  return (
    <button
      data-testid={testId}
      type="button"
      role="radio"
      aria-checked={isActive}
      onClick={onClick}
      className={cn(
        'whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
        isActive
          ? 'border-admin-lime bg-admin-lime text-primary-foreground'
          : 'border-border bg-card text-muted-foreground hover:bg-admin-surface-muted hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}
